import type { ComponentProps } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { typographyVariants, type TypographyVariant } from './typography'

const cardVariants = cva(
  'flex flex-col rounded-xl border border-border/70 bg-card text-card-foreground shadow-none',
  {
    variants: {
      accent: {
        none: '',
        brand:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-brand-amethyst before:via-brand-amethyst/30 before:to-transparent',
        hero: 'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-brand-amethyst before:via-brand-amethyst/30 before:to-transparent',
        amethyst:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-brand-amethyst before:via-brand-amethyst/30 before:to-transparent',
        periwinkle:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-brand-periwinkle before:via-brand-periwinkle/30 before:to-transparent',
        payment:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-brand-periwinkle before:via-brand-periwinkle/30 before:to-transparent',
        arrangementPhotos:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-brand-periwinkle before:via-brand-periwinkle/30 before:to-transparent',
        mint: 'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-chart-3 before:via-chart-3/25 before:to-transparent',
        purchaser:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-chart-3 before:via-chart-3/25 before:to-transparent',
        personalTouches:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-brand-amethyst before:via-brand-amethyst/30 before:to-transparent',
        info: 'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-status-info-foreground before:via-status-info-border/30 before:to-transparent',
        warning:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-status-warning-foreground before:via-status-warning-border/30 before:to-transparent',
        destructive:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-status-destructive-foreground before:via-status-destructive-border/30 before:to-transparent',
        success:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-status-success-foreground before:via-status-success-border/30 before:to-transparent',
        neutral:
          'relative before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-linear-to-r before:from-muted-foreground/55 before:via-border/25 before:to-transparent',
      },
      interactive: {
        true: 'transition-[border-color,box-shadow] hover:border-selection-border hover:shadow-md',
        false: '',
      },
      selected: {
        true: 'border-primary/35 bg-primary/[0.035] shadow-sm',
        false: '',
      },
      surface: {
        default: '',
        background: 'bg-background',
        brand: 'border-primary/20',
        brandSubtle: 'border-selection-border/25 bg-accentSecondary/45',
        elevated: 'bg-elevated',
        subtle: 'border-border/40 bg-muted/20 dark:border-border/50 dark:bg-elevated/70',
        warning: 'border-status-warning-border bg-status-warning-background',
        destructive: 'border-status-destructive-border bg-status-destructive-background',
        info: 'border-status-info-border bg-status-info-background',
        success: 'border-status-success-border bg-status-success-background',
        infoSubtle:
          'border-status-info-border bg-status-info-background dark:border-border/45 dark:bg-card',
        successSubtle:
          'border-status-success-border bg-status-success-background dark:border-border/45 dark:bg-card',
        destructiveSubtle:
          'border-status-destructive-border bg-status-destructive-background dark:border-border/45 dark:bg-card',
        preview: 'bg-zinc-950',
        transparent: 'border-transparent bg-transparent',
        bare: 'rounded-none border-0 bg-transparent shadow-none',
      },
      rail: {
        none: '',
        brand:
          'relative after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-1 after:bg-brand-amethyst',
        info: 'relative after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-1 after:bg-status-info-foreground',
        success:
          'relative after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-1 after:bg-status-success-foreground',
        warning:
          'relative after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-1 after:bg-status-warning-foreground',
        destructive:
          'relative after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-1 after:bg-status-destructive-foreground',
      },
      size: {
        default: 'gap-6 py-6',
        flush: 'gap-0 py-0',
        xs: 'gap-2 py-2',
        sm: 'gap-4 py-4',
        lg: 'gap-8 py-8',
        xl: 'gap-10 py-10',
      },
    },
    defaultVariants: {
      accent: 'none',
      interactive: false,
      selected: false,
      surface: 'default',
      rail: 'none',
      size: 'default',
    },
  }
)

const cardHeaderVariants = cva(
  '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
  {
    variants: {
      size: {
        default: 'px-6',
        xs: 'px-2',
        sm: 'px-4',
        lg: 'px-8',
        xl: 'px-10',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

const cardContentVariants = cva('', {
  variants: {
    size: {
      default: 'px-6',
      xs: 'px-2',
      sm: 'px-4',
      lg: 'px-8',
      xl: 'px-10',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const cardFooterVariants = cva('flex items-center [.border-t]:pt-6', {
  variants: {
    size: {
      default: 'px-6',
      xs: 'px-2',
      sm: 'px-4',
      lg: 'px-8',
      xl: 'px-10',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

export type CardProps = ComponentProps<'div'> & VariantProps<typeof cardVariants>

export type CardSectionSize = 'default' | 'xs' | 'sm' | 'lg' | 'xl'

export type CardHeaderProps = ComponentProps<'div'> & VariantProps<typeof cardHeaderVariants>

export type CardTitleProps = ComponentProps<'div'> & {
  size?: CardSectionSize
}

export type CardDescriptionProps = ComponentProps<'div'> & {
  size?: CardSectionSize
}

export type CardActionProps = ComponentProps<'div'>

export type CardContentProps = ComponentProps<'div'> & VariantProps<typeof cardContentVariants>

export type CardFooterProps = ComponentProps<'div'> & VariantProps<typeof cardFooterVariants>

function Card({
  accent = 'none',
  className = '',
  interactive = false,
  selected = false,
  rail = 'none',
  size = 'default',
  surface = 'default',
  ...props
}: CardProps) {
  return (
    <div
      data-slot="card"
      data-accent={accent}
      data-interactive={interactive ? 'true' : 'false'}
      data-selected={selected ? 'true' : 'false'}
      data-rail={rail}
      data-surface={surface}
      className={cn(
        cardVariants({ accent, interactive, selected, rail, size, surface, className })
      )}
      {...props}
    />
  )
}

function CardHeader({ className = '', size = 'default', ...props }: CardHeaderProps) {
  return (
    <div
      data-slot="card-header"
      className={cn(cardHeaderVariants({ size, className }))}
      {...props}
    />
  )
}

function CardTitle({ className = '', size = 'default', ...props }: CardTitleProps) {
  const titleVariants: Record<CardSectionSize, TypographyVariant> = {
    default: 'h3',
    xs: 'h5',
    sm: 'h4',
    lg: 'h2',
    xl: 'h1',
  }

  return (
    <div
      data-slot="card-title"
      className={cn(typographyVariants({ variant: titleVariants[size] ?? 'h3' }), className)}
      {...props}
    />
  )
}

function CardDescription({ className = '', size = 'default', ...props }: CardDescriptionProps) {
  const descriptionSizeClasses: Record<CardSectionSize, string> = {
    default: 'text-sm',
    xs: 'text-xs',
    sm: 'text-xs',
    lg: 'text-base',
    xl: 'text-lg',
  }

  return (
    <div
      data-slot="card-description"
      className={cn(
        typographyVariants({ variant: 'description' }),
        descriptionSizeClasses[size],
        className
      )}
      {...props}
    />
  )
}

function CardAction({ className = '', ...props }: CardActionProps) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  )
}

function CardContent({ className = '', size = 'default', ...props }: CardContentProps) {
  return (
    <div
      data-slot="card-content"
      className={cn(cardContentVariants({ size, className }))}
      {...props}
    />
  )
}

function CardFooter({ className = '', size = 'default', ...props }: CardFooterProps) {
  return (
    <div
      data-slot="card-footer"
      className={cn(cardFooterVariants({ size, className }))}
      {...props}
    />
  )
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  cardVariants,
}
